import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, 
  Lightbulb, 
  Upload, 
  X, 
  ArrowRight, 
  Loader2,
  Globe
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewProject() {
  const [step, setStep] = useState(1);
  const [projectData, setProjectData] = useState({
    name: "",
    description: "",
    idea_text: "",
    language: "tr"
  });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch current user
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validTypes = [".pdf", ".docx", ".xlsx", ".pptx", ".txt"];
    
    const validFiles = selectedFiles.filter(file => {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      return validTypes.includes(ext);
    });

    if (validFiles.length !== selectedFiles.length) {
      toast.error("Bazı dosyalar desteklenmiyor. Desteklenen: PDF, DOCX, XLSX, PPTX, TXT");
    }

    setFiles([...files, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleCreate = async () => {
    if (!projectData.name.trim()) {
      toast.error("Proje adı gerekli");
      return;
    }

    if (!projectData.idea_text.trim() && files.length === 0) {
      toast.error("En az bir fikir metni veya doküman gerekli");
      return;
    }

    setCreating(true);
    try {
      // Create project
      const projectResponse = await axios.post(`${API}/projects`, projectData, {
        withCredentials: true
      });
      const projectId = projectResponse.data.id;

      // Upload files if any
      if (files.length > 0) {
        setUploading(true);
        for (const file of files) {
          const formData = new FormData();
          formData.append("file", file);
          await axios.post(`${API}/projects/${projectId}/documents`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true
          });
        }
        setUploading(false);
      }

      toast.success("Proje oluşturuldu!");
      navigate(`/project/${projectId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Proje oluşturulamadı");
    } finally {
      setCreating(false);
      setUploading(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Yeni Proje Oluştur</h1>
        <p className="text-muted-foreground mt-1">
          İş analizi için gerekli bilgileri girin.
        </p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-4">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div 
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div className={`w-12 h-0.5 ${step > s ? "bg-primary" : "bg-secondary"}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Project Info */}
      {step === 1 && (
        <Card className="glass-card animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Proje Bilgileri
            </CardTitle>
            <CardDescription>Projenizin temel bilgilerini girin.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="project-name">Proje Adı *</Label>
              <Input
                id="project-name"
                placeholder="Örn: E-Ticaret Platformu Analizi"
                value={projectData.name}
                onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                data-testid="project-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-desc">Kısa Açıklama</Label>
              <Input
                id="project-desc"
                placeholder="Projenin kısa açıklaması"
                value={projectData.description}
                onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                data-testid="project-desc-input"
              />
            </div>

            <div className="space-y-3">
              <Label>Rapor Dili</Label>
              <RadioGroup
                value={projectData.language}
                onValueChange={(value) => setProjectData({ ...projectData, language: value })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="tr" id="lang-tr" data-testid="lang-tr" />
                  <Label htmlFor="lang-tr" className="flex items-center gap-2 cursor-pointer">
                    <Globe className="w-4 h-4" />
                    Türkçe
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="en" id="lang-en" data-testid="lang-en" />
                  <Label htmlFor="lang-en" className="flex items-center gap-2 cursor-pointer">
                    <Globe className="w-4 h-4" />
                    English
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!projectData.name.trim()} data-testid="next-step-1-btn">
                Devam
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Idea/Description */}
      {step === 2 && (
        <Card className="glass-card animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-primary" />
              Proje Fikri
            </CardTitle>
            <CardDescription>
              Projenizin fikrini, hedeflerini ve gereksinimlerini detaylı olarak yazın. 
              Doküman olmasa bile bu metin üzerinden tam bir analiz üretebiliriz.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Textarea
              placeholder="Projenizin amacı, hedefleri, kullanıcı senaryoları, teknik gereksinimler..."
              className="min-h-[300px] resize-none"
              value={projectData.idea_text}
              onChange={(e) => setProjectData({ ...projectData, idea_text: e.target.value })}
              data-testid="idea-text-input"
            />

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="prev-step-2-btn">
                Geri
              </Button>
              <Button onClick={() => setStep(3)} data-testid="next-step-2-btn">
                Devam
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Documents */}
      {step === 3 && (
        <Card className="glass-card animate-fade-in">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Dokümanlar
            </CardTitle>
            <CardDescription>
              Varsa ilgili dokümanları yükleyin. SRS, sözleşme, teklif, toplantı notları vb.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => document.getElementById("file-input").click()}
              data-testid="file-upload-area"
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.pptx,.txt"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="file-input"
              />
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <p className="text-foreground font-medium">Dosya yüklemek için tıklayın</p>
              <p className="text-sm text-muted-foreground mt-1">
                PDF, DOCX, XLSX, PPTX, TXT desteklenir
              </p>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="space-y-2">
                <Label>Yüklenen Dosyalar ({files.length})</Label>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                      data-testid={`uploaded-file-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-primary" />
                        <span className="text-sm truncate max-w-[300px]">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        data-testid={`remove-file-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="prev-step-3-btn">
                Geri
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={creating || uploading}
                className="btn-glow"
                data-testid="create-project-btn"
              >
                {creating || uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {uploading ? "Yükleniyor..." : "Oluşturuluyor..."}
                  </>
                ) : (
                  <>
                    Proje Oluştur
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
